import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AppProvider } from "@/context/AppContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import VerifyEmail from "@/pages/VerifyEmail";
import Activate from "@/pages/Activate";
import Users from "@/pages/Users";
import Home from "@/pages/Home";
import Finances from "@/pages/Finances";
import ActionPage from "@/pages/ActionPage";
import Announcements from "@/pages/Announcements";
import More from "@/pages/More";
import Tickets from "@/pages/Tickets";
import Lots from "@/pages/Lots";
import Professionals from "@/pages/Professionals";
import Residents from "@/pages/Residents";
import Documents from "@/pages/Documents";
import AuditLog from "@/pages/AuditLog";
import Assemblies from "@/pages/Assemblies";
import AssemblyDetail from "@/pages/AssemblyDetail";
import Maintenance from "@/pages/Maintenance";
import Legal from "@/pages/Legal";
import Backup from "@/pages/Backup";
import ChangePinScreen from "@/components/ChangePinScreen";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-10 w-10 rounded-full border-4 border-brand border-t-transparent animate-spin" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  // Changement de PIN OBLIGATOIRE : on bloque toutes les pages tant que ce n'est pas fait.
  if (user.must_change_pin) return <ChangePinScreen />;
  return <Layout>{children}</Layout>;
}

// Garde sans Layout (plein écran) — utilisée pour le changement de PIN volontaire.
function ProtectedBare({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-10 w-10 rounded-full border-4 border-brand border-t-transparent animate-spin" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      {/* Public — connexion directe (résidence unique) */}
      <Route path="/" element={user ? <Navigate to="/app" replace /> : <Login />} />
      <Route path="/login" element={user ? <Navigate to="/app" replace /> : <Login />} />
      <Route path="/register" element={user ? <Navigate to="/app" replace /> : <Register />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/activate" element={<Activate />} />

      {/* Espace résident (protégé) */}
      <Route path="/app" element={<Protected><Home /></Protected>} />
      <Route path="/users" element={<Protected><Users /></Protected>} />
      <Route path="/finances" element={<Protected><Finances /></Protected>} />
      <Route path="/action" element={<Protected><ActionPage /></Protected>} />
      <Route path="/announcements" element={<Protected><Announcements /></Protected>} />
      <Route path="/more" element={<Protected><More /></Protected>} />
      <Route path="/tickets" element={<Protected><Tickets /></Protected>} />
      <Route path="/lots" element={<Protected><Lots /></Protected>} />
      <Route path="/professionals" element={<Protected><Professionals /></Protected>} />
      <Route path="/residents" element={<Protected><Residents /></Protected>} />
      <Route path="/documents" element={<Protected><Documents /></Protected>} />
      <Route path="/audit" element={<Protected><AuditLog /></Protected>} />
      <Route path="/assemblies" element={<Protected><Assemblies /></Protected>} />
      <Route path="/assemblies/:id" element={<Protected><AssemblyDetail /></Protected>} />
      <Route path="/maintenance" element={<Protected><Maintenance /></Protected>} />
      <Route path="/legal" element={<Protected><Legal /></Protected>} />
      <Route path="/backup" element={<Protected><Backup /></Protected>} />
      <Route path="/change-pin" element={<ProtectedBare><ChangePinScreen /></ProtectedBare>} />
      <Route path="*" element={<Navigate to={user ? "/app" : "/"} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AppProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" richColors duration={6000} closeButton />
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </AppProvider>
  );
}

export default App;
